import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { Link, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useWS } from '../context/WSContext';
import { UserPlus, Trash2, Key, Ban, MessageCircle, Send, X, Megaphone, Save, ChevronRight, Search, Users, Circle, ExternalLink, Film, Pin, EyeOff, Eye, Edit, Bell, Image as ImageIcon, StickyNote, Home as HomeIcon } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import Avatar from '../components/Avatar';
import PushEnableBanner from '../components/PushEnableBanner';
import AdminHomeLayout from '../components/AdminHomeLayout';
import { formatLastSeen } from '../lib/time';
import AdminContents from '../components/AdminContents';
import AdminApps from '../components/AdminApps';
import AdminBroadcasts from '../components/AdminBroadcasts';
import Admin2FA from '../components/Admin2FA';
import AdminProfile from '../components/AdminProfile';
import AdminManagers from '../components/AdminManagers';
import AdminPushTest from '../components/AdminPushTest';

export default function Admin() {
  const { user } = useAuth();
  const { subscribe, presence } = useWS();
  const [users, setUsers] = useState([]);
  const [chats, setChats] = useState([]);
  const [tab, setTab] = useState('chats');
  const [showCreate, setShowCreate] = useState(false);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [pwEditFor, setPwEditFor] = useState(null);
  const [newUser, setNewUser] = useState({ username: '', password: '', is_admin: false, notes: '' });
  const [broadcast, setBroadcast] = useState('');
  const [code, setCode] = useState('');
  const [newPw, setNewPw] = useState('');
  const [search, setSearch] = useState('');
  const [chatSearch, setChatSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | online | offline | blocked
  const navigate = useNavigate();
  const [deleteTarget, setDeleteTarget] = useState(null); // {id, username}
  const [notesEditFor, setNotesEditFor] = useState(null); // user object
  const [notesDraft, setNotesDraft] = useState('');
  const [showLinks, setShowLinks] = useState(false);
  const [settings, setSettings] = useState({
    downloader_code: '',
    app_golden_tv_url: '',
    app_infinity_golden_url: '',
    download_page_url: '',
  });

  const loadAll = async () => {
    try {
      const [u, c, s] = await Promise.all([
        api.get('/users'),
        api.get('/chats'),
        api.get('/settings'),
      ]);
      setUsers(u.data);
      setChats(c.data);
      setCode(s.data.downloader_code || '');
      setSettings(s.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { loadAll(); }, []);

  // Filter + alphabetical sort for the Utenti tab
  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = users.filter((u) => {
      if (q && !u.username.toLowerCase().includes(q)) return false;
      const isOnline = presence[u.id]?.online ?? u.is_online;
      if (filter === 'online' && (!isOnline || u.is_blocked)) return false;
      if (filter === 'offline' && (isOnline || u.is_blocked)) return false;
      if (filter === 'blocked' && !u.is_blocked) return false;
      return true;
    });
    list.sort((a, b) => a.username.localeCompare(b.username, 'it', { sensitivity: 'base' }));
    return list;
  }, [users, search, filter, presence]);

  const filterButtons = [
    { key: 'all', label: 'Tutti' },
    { key: 'online', label: 'Online' },
    { key: 'offline', label: 'Offline' },
    { key: 'blocked', label: 'Bloccati' },
  ];

  const counts = useMemo(() => {
    let online = 0, offline = 0, blocked = 0;
    for (const u of users) {
      if (u.is_blocked) { blocked++; continue; }
      if (presence[u.id]?.online ?? u.is_online) online++;
      else offline++;
    }
    return { all: users.length, online, offline, blocked };
  }, [users, presence]);

  useEffect(() => {
    const unsub = subscribe((data) => {
      if (data.type === 'message') loadAll();
    });
    return unsub;
    // eslint-disable-next-line
  }, []);

  const createUser = async () => {
    if (!newUser.username || newUser.password.length < 6) return toast.error('Compila i campi (password min 6 caratteri)');
    try {
      await api.post('/users', newUser);
      toast.success(newUser.is_admin ? 'Admin creato — cambio password richiesto al primo login' : 'Utente creato');
      setNewUser({ username: '', password: '', is_admin: false, notes: '' });
      setShowCreate(false);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };

  const del = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success('Utente eliminato');
      setDeleteTarget(null);
      loadAll();
    } catch { toast.error('Errore'); }
  };

  const saveNotes = async () => {
    if (!notesEditFor) return;
    try {
      await api.put(`/users/${notesEditFor.id}/notes`, { notes: notesDraft });
      toast.success('Nota salvata');
      setNotesEditFor(null);
      setNotesDraft('');
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };

  const toggleBlock = async (id) => {
    try {
      await api.put(`/users/${id}/block`);
      loadAll();
    } catch { toast.error('Errore'); }
  };

  const changePw = async () => {
    if (newPw.length < 3) return toast.error('Password troppo corta');
    try {
      await api.put(`/users/${pwEditFor}/password`, { password: newPw });
      toast.success('Password aggiornata');
      setPwEditFor(null);
      setNewPw('');
    } catch { toast.error('Errore'); }
  };

  const sendBroadcast = async () => {
    if (!broadcast.trim()) return;
    try {
      const { data } = await api.post('/messages/broadcast', { content: broadcast.trim(), type: 'text' });
      toast.success(`Inviato a ${data.sent} utenti`);
      setBroadcast('');
      setShowBroadcast(false);
    } catch { toast.error('Errore invio'); }
  };

  const saveCode = async () => {
    try {
      await api.put('/settings', { updates: { downloader_code: code } });
      toast.success('Codice aggiornato');
      setShowCode(false);
      loadAll();
    } catch { toast.error('Errore'); }
  };

  const saveLinks = async () => {
    try {
      await api.put('/settings', {
        updates: {
          app_golden_tv_url: settings.app_golden_tv_url || '',
          app_infinity_golden_url: settings.app_infinity_golden_url || '',
          download_page_url: settings.download_page_url || '',
        },
      });
      toast.success('Link aggiornati');
      setShowLinks(false);
      loadAll();
    } catch { toast.error('Errore'); }
  };

  return (
    <Layout title="Pannello Admin">
      <div className="px-4 pt-4 max-w-2xl mx-auto">
        <div className="text-xs uppercase tracking-[0.3em] text-white/40 mb-1">Amministratore</div>
        <h2 className="font-heading text-2xl font-light mb-5">
          <span className="text-[var(--gold)]">{user?.username}</span>
        </h2>

        <div className="mb-4">
          <PushEnableBanner />
        </div>

        <div className="flex gap-2 mb-4 overflow-x-auto hide-scrollbar">
          {[
            { k: 'chats', label: 'Chat', icon: MessageCircle },
            { k: 'users', label: 'Utenti', icon: UserPlus },
            { k: 'contents', label: 'Contenuti', icon: Film },
            { k: 'home', label: 'Home', icon: HomeIcon },
            { k: 'tools', label: 'Strumenti', icon: Megaphone },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`admin-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`px-4 py-2 rounded-full text-sm flex items-center gap-2 transition-colors whitespace-nowrap ${tab === t.k ? 'bg-[var(--gold)] text-black' : 'bg-[var(--bg-surface)] text-white/70 border border-[var(--border-subtle)]'}`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'chats' && (
          <div className="space-y-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                data-testid="admin-chat-search"
                type="text"
                value={chatSearch}
                onChange={(e) => setChatSearch(e.target.value)}
                placeholder="Cerca fra le chat…"
                className="input-gold pl-9"
              />
            </div>
            {(() => {
              const q = chatSearch.trim().toLowerCase();
              const filtered = q
                ? chats.filter((c) => {
                    const inUser = c.username && c.username.toLowerCase().includes(q);
                    const lastContent = c.last_message?.content?.toLowerCase() || '';
                    return inUser || lastContent.includes(q);
                  })
                : chats;
              if (chats.length === 0) return <div className="text-center text-white/40 text-sm py-8">Nessuna chat ancora.</div>;
              if (filtered.length === 0) return <div className="text-center text-white/40 text-sm py-8" data-testid="admin-chats-empty-search">Nessuna chat corrisponde a "{chatSearch}"</div>;
              return filtered.map((c) => {
                const online = presence[c.id]?.online ?? c.is_online;
                return (
                  <Link
                    key={c.id}
                    to={`/admin/chat/${c.id}`}
                    data-testid={`admin-chat-${c.username}`}
                    className="card-surface p-3 flex items-center gap-3 block"
                  >
                    <div className="relative">
                      <Avatar username={c.username} avatarUrl={c.avatar_url} size={44} />
                      <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-[var(--bg-void)] ${online ? 'bg-[var(--success)]' : 'bg-white/30'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="font-heading font-medium text-white truncate">{c.username}</div>
                        {c.unread > 0 && (
                          <span className="text-[10px] bg-[var(--gold)] text-black rounded-full px-2 py-0.5 font-bold ml-2">{c.unread}</span>
                        )}
                      </div>
                      <div className="text-xs text-white/50 truncate">
                        {c.last_message
                          ? (c.last_message.encrypted
                              ? '🔒 Messaggio cifrato'
                              : (c.last_message.content || `[${c.last_message.type}]`))
                          : 'Nessun messaggio'}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-white/30" />
                  </Link>
                );
              });
            })()}
          </div>
        )}

        {tab === 'users' && (
          <div className="space-y-3">
            <div
              data-testid="users-stats-bar"
              className="card-surface p-3 flex items-center justify-around text-sm"
            >
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-[var(--gold)]" strokeWidth={2} />
                <span className="text-white/60">Totale:</span>
                <span data-testid="stats-total" className="font-heading font-semibold text-white">{counts.all}</span>
              </div>
              <div className="w-px h-5 bg-[var(--border-subtle)]" />
              <div className="flex items-center gap-2">
                <Circle className="w-3 h-3 fill-[var(--success)] text-[var(--success)]" />
                <span className="text-white/60">Online:</span>
                <span data-testid="stats-online" className="font-heading font-semibold text-white">{counts.online}</span>
              </div>
            </div>

            <button
              data-testid="admin-create-user-btn"
              onClick={() => setShowCreate(true)}
              className="btn-gold w-full py-3 flex items-center justify-center gap-2"
            >
              <UserPlus className="w-4 h-4" /> Nuovo utente
            </button>

            <div className="relative">
              <Search className="w-4 h-4 text-white/40 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                data-testid="users-search-input"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca utente…"
                className="input-gold pl-11"
              />
            </div>

            <div className="flex gap-2 overflow-x-auto hide-scrollbar -mx-1 px-1">
              {filterButtons.map((f) => {
                const active = filter === f.key;
                const n = counts[f.key];
                return (
                  <button
                    key={f.key}
                    data-testid={`users-filter-${f.key}`}
                    onClick={() => setFilter(f.key)}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium tracking-wide whitespace-nowrap transition-colors flex items-center gap-2 ${
                      active
                        ? 'bg-[var(--gold)] text-black border border-[var(--gold)]'
                        : 'bg-[var(--bg-surface)] text-white/70 border border-[var(--border-subtle)] hover:border-[var(--gold)]/40'
                    }`}
                  >
                    <span>{f.label}</span>
                    <span
                      data-testid={`users-filter-count-${f.key}`}
                      className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold min-w-[18px] text-center ${
                        active ? 'bg-black/20 text-black' : 'bg-[var(--gold-soft)] text-[var(--gold)]'
                      }`}
                    >
                      {n}
                    </span>
                  </button>
                );
              })}
            </div>

            {filteredUsers.map((u) => {
              const isOnline = presence[u.id]?.online ?? u.is_online;
              const lastSeenLabel = formatLastSeen(u.last_seen);
              return (
                <div
                  key={u.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => !u.is_admin && navigate(`/admin/chat/${u.id}`)}
                  onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !u.is_admin) navigate(`/admin/chat/${u.id}`); }}
                  className={`card-surface p-3 flex items-center gap-3 ${u.is_admin ? '' : 'cursor-pointer hover:border-[var(--gold)]/30 transition-colors'}`}
                  data-testid={`user-row-${u.username}`}
                >
                  <div className="relative">
                    <Avatar username={u.username} size={44} avatarUrl={u.avatar_url} />
                    <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-[var(--bg-void)] ${isOnline ? 'bg-[var(--success)]' : 'bg-white/30'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-white truncate flex items-center gap-2">
                      {u.username}
                      {u.is_admin && <span className="text-[9px] bg-[var(--gold)] text-black px-1.5 py-0.5 rounded-full font-bold tracking-widest">ADMIN</span>}
                    </div>
                    <div className="text-[11px] text-white/50 truncate">
                      {u.is_blocked ? (
                        <span className="text-red-400">Bloccato</span>
                      ) : isOnline ? (
                        <span className="text-[var(--success)]">online</span>
                      ) : (
                        <span>Ultimo accesso: {lastSeenLabel || 'mai'}</span>
                      )}
                    </div>
                    {u.notes && (
                      <div
                        data-testid={`user-note-${u.username}`}
                        className="text-[11px] text-[var(--gold)]/85 mt-1 flex items-start gap-1 leading-snug"
                        title={u.notes}
                      >
                        <StickyNote className="w-3 h-3 flex-shrink-0 mt-[1px]" />
                        <span className="line-clamp-2 break-words">{u.notes}</span>
                      </div>
                    )}
                  </div>
                  {!u.is_admin && (
                    <button
                      data-testid={`user-chat-${u.username}`}
                      onClick={(e) => { e.stopPropagation(); navigate(`/admin/chat/${u.id}`); }}
                      className="p-2 text-white/60 hover:text-[var(--gold)]"
                      title="Apri chat"
                    >
                      <MessageCircle className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    data-testid={`user-note-btn-${u.username}`}
                    onClick={(e) => { e.stopPropagation(); setNotesEditFor(u); setNotesDraft(u.notes || ''); }}
                    className={`p-2 ${u.notes ? 'text-[var(--gold)]' : 'text-white/60'} hover:text-[var(--gold)]`}
                    title="Nota privata"
                  >
                    <StickyNote className="w-4 h-4" />
                  </button>
                  <button data-testid={`user-pw-${u.username}`} onClick={(e) => { e.stopPropagation(); setPwEditFor(u.id); }} className="p-2 text-white/60 hover:text-[var(--gold)]" title="Password"><Key className="w-4 h-4" /></button>
                  <button data-testid={`user-block-${u.username}`} onClick={(e) => { e.stopPropagation(); toggleBlock(u.id); }} className={`p-2 ${u.is_blocked ? 'text-red-400' : 'text-white/60'} hover:text-red-400`} title="Blocca"><Ban className="w-4 h-4" /></button>
                  <button data-testid={`user-delete-${u.username}`} onClick={(e) => { e.stopPropagation(); setDeleteTarget({ id: u.id, username: u.username }); }} className="p-2 text-white/60 hover:text-red-400" title="Elimina"><Trash2 className="w-4 h-4" /></button>
                </div>
              );
            })}
            {filteredUsers.length === 0 && users.length > 0 && (
              <div className="text-center text-white/40 text-sm py-8" data-testid="users-empty-search">Nessun utente trovato.</div>
            )}
            {users.length === 0 && <div className="text-center text-white/40 text-sm py-8">Nessun utente ancora. Creane uno!</div>}
          </div>
        )}

        {tab === 'contents' && (
          <AdminContents settings={settings} onSettingsSaved={loadAll} />
        )}

        {tab === 'home' && <AdminHomeLayout />}

        {tab === 'tools' && (
          <div className="space-y-3">
            <AdminProfile />

            <div>
              <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Sicurezza account</div>
              <Admin2FA />
            </div>

            <div className="pt-1">
              <AdminPushTest />
            </div>

            <div className="pt-1">
              <AdminManagers />
            </div>

            <div>
              <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Comunicazioni Importanti</div>
              <AdminBroadcasts />
            </div>

            <button data-testid="code-open-btn" onClick={() => setShowCode(true)} className="card-surface p-4 w-full flex items-center gap-4 text-left">
              <Key className="w-5 h-5 text-[var(--gold)]" />
              <div className="flex-1">
                <div className="font-heading font-medium">Codice Downloader</div>
                <div className="text-xs text-white/50">Attuale: <span className="text-[var(--gold)]">{code || '—'}</span></div>
              </div>
            </button>
            <button data-testid="links-open-btn" onClick={() => setShowLinks(true)} className="card-surface p-4 w-full flex items-center gap-4 text-left">
              <ExternalLink className="w-5 h-5 text-[var(--gold)]" />
              <div className="flex-1">
                <div className="font-heading font-medium">Pagina Download generale</div>
                <div className="text-xs text-white/50">URL usato dal pulsante "Apri pagina Download"</div>
              </div>
            </button>

            <div className="pt-2">
              <div className="text-xs uppercase tracking-widest text-white/50 mb-3">Gestione applicazioni</div>
              <AdminApps />
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <AnimatePresence>
        {showCreate && (
          <Modal onClose={() => setShowCreate(false)} title="Nuovo utente">
            <input data-testid="new-user-username" className="input-gold" placeholder="Username" value={newUser.username} onChange={(e) => setNewUser({ ...newUser, username: e.target.value })} />
            <PasswordInput
              testId="new-user-password"
              placeholder="Password (min 6)"
              value={newUser.password}
              onChange={(v) => setNewUser({ ...newUser, password: v })}
              allowGenerate
            />
            <div>
              <textarea
                data-testid="new-user-notes"
                className="input-gold min-h-[80px] resize-y leading-relaxed"
                placeholder="Nota privata (opzionale) — es. nome reale, città, come ti ha contattato…"
                maxLength={1000}
                value={newUser.notes}
                onChange={(e) => setNewUser({ ...newUser, notes: e.target.value })}
              />
              <p className="text-[10px] text-white/40 mt-1 flex items-center gap-1">
                <StickyNote className="w-3 h-3" /> Visibile solo agli amministratori. L'utente non la vedrà mai.
              </p>
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                data-testid="new-user-is-admin"
                type="checkbox"
                checked={newUser.is_admin}
                onChange={(e) => setNewUser({ ...newUser, is_admin: e.target.checked })}
                className="w-4 h-4 accent-[var(--gold)]"
              />
              <span className="text-sm text-white/85">Concedi accesso <span className="text-[var(--gold)] font-semibold">Amministratore</span></span>
            </label>
            {newUser.is_admin && (
              <p className="text-[10px] text-white/50 leading-relaxed">Il nuovo admin dovrà cambiare la password al primo accesso. Riceverà tutti i permessi tuoi (chat, contenuti, utenti, broadcast).</p>
            )}
            <button data-testid="new-user-submit" onClick={createUser} className="btn-gold w-full py-3">Crea</button>
          </Modal>
        )}
        {deleteTarget && (
          <Modal onClose={() => setDeleteTarget(null)} title="Conferma eliminazione">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-red-400" />
              </div>
              <p className="text-sm text-white/80 leading-relaxed pt-1" data-testid="delete-confirm-text">
                Eliminare definitivamente l'utente <span className="font-semibold text-[var(--gold)]">"{deleteTarget.username}"</span>?
                <br />
                <span className="text-xs text-white/50">L'operazione non può essere annullata.</span>
              </p>
            </div>
            <div className="flex gap-3 pt-1">
              <button
                data-testid="delete-cancel-btn"
                onClick={() => setDeleteTarget(null)}
                className="flex-1 py-3 rounded-full border border-[var(--border-subtle)] text-white/80 hover:border-[var(--gold)]/50 hover:text-white transition-colors text-sm font-medium"
              >
                Annulla
              </button>
              <button
                data-testid="delete-confirm-btn"
                onClick={() => del(deleteTarget.id)}
                className="flex-1 py-3 rounded-full bg-red-500 text-white font-semibold hover:bg-red-600 transition-colors text-sm"
              >
                Elimina
              </button>
            </div>
          </Modal>
        )}
        {pwEditFor && (
          <Modal onClose={() => setPwEditFor(null)} title="Modifica password">
            <PasswordInput
              testId="edit-password-input"
              placeholder="Nuova password"
              value={newPw}
              onChange={setNewPw}
              allowGenerate
            />
            <button data-testid="edit-password-submit" onClick={changePw} className="btn-gold w-full py-3">Salva</button>
          </Modal>
        )}
        {notesEditFor && (
          <Modal onClose={() => setNotesEditFor(null)} title={`Nota privata — ${notesEditFor.username}`}>
            <textarea
              data-testid="edit-notes-input"
              className="input-gold min-h-[140px] resize-y leading-relaxed"
              placeholder="Es. Mario Rossi — Milano — contatto Telegram 4 gen"
              maxLength={1000}
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              autoFocus
            />
            <p className="text-[10px] text-white/40 flex items-center gap-1">
              <StickyNote className="w-3 h-3" /> Visibile solo a te e agli altri admin. L'utente non vede nulla.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setNotesEditFor(null)}
                className="flex-1 py-3 rounded-full border border-[var(--border-subtle)] text-white/80 text-sm"
              >
                Annulla
              </button>
              <button data-testid="edit-notes-submit" onClick={saveNotes} className="flex-1 btn-gold py-3">
                Salva
              </button>
            </div>
          </Modal>
        )}
        {showBroadcast && (
          <Modal onClose={() => setShowBroadcast(false)} title="Broadcast (deprecato)">
            <p className="text-sm text-white/60">Usa il nuovo sistema Broadcast integrato in questa scheda.</p>
          </Modal>
        )}
        {showCode && (
          <Modal onClose={() => setShowCode(false)} title="Codice Downloader">
            <input data-testid="code-input" className="input-gold" placeholder="Es. 123456" value={code} onChange={(e) => setCode(e.target.value)} />
            <button data-testid="code-save-btn" onClick={saveCode} className="btn-gold w-full py-3 flex items-center justify-center gap-2">
              <Save className="w-4 h-4" /> Salva
            </button>
          </Modal>
        )}
        {showLinks && (
          <Modal onClose={() => setShowLinks(false)} title="Pagina Download generale">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/50 block mb-1">URL pagina download</label>
              <input
                data-testid="link-download-page-input"
                className="input-gold"
                placeholder="https://…"
                value={settings.download_page_url || ''}
                onChange={(e) => setSettings({ ...settings, download_page_url: e.target.value })}
              />
              <p className="text-[10px] text-white/40 mt-2">Usato dal pulsante "Apri pagina Download" nella schermata cliente. Le singole app hanno il loro URL editabile qui sotto in "Gestione applicazioni".</p>
            </div>
            <button data-testid="links-save-btn" onClick={saveLinks} className="btn-gold w-full py-3 flex items-center justify-center gap-2">
              <Save className="w-4 h-4" /> Salva
            </button>
          </Modal>
        )}
      </AnimatePresence>
    </Layout>
  );
}

function Modal({ children, title, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 40, scale: 0.98 }}
        animate={{ y: 0, scale: 1 }}
        exit={{ y: 40, scale: 0.98 }}
        onClick={(e) => e.stopPropagation()}
        className="card-surface p-5 w-full max-w-sm space-y-4"
      >
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg font-medium">{title}</h3>
          <button data-testid="modal-close-btn" onClick={onClose} className="text-white/50 hover:text-white p-1"><X className="w-4 h-4" /></button>
        </div>
        {children}
      </motion.div>
    </motion.div>
  );
}

/**
 * Password input with show/hide toggle and optional password generator.
 * When shown, the value is visible so the admin can double-check before saving.
 */
function PasswordInput({ value, onChange, placeholder, testId, allowGenerate = false }) {
  const [show, setShow] = useState(false);

  const generate = () => {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    let out = '';
    for (let i = 0; i < 10; i++) out += chars.charAt(Math.floor(Math.random() * chars.length));
    onChange(out);
    setShow(true);
  };

  return (
    <div className="relative">
      <input
        data-testid={testId}
        type={show ? 'text' : 'password'}
        className={`input-gold ${show ? 'font-mono tracking-wide' : ''}`}
        style={{ paddingRight: allowGenerate ? '5.5rem' : '3rem' }}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="new-password"
      />
      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
        {allowGenerate && (
          <button
            type="button"
            data-testid={`${testId}-generate`}
            onClick={generate}
            className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-md text-[var(--gold)] hover:bg-[var(--gold)]/10"
            title="Genera password"
          >
            Genera
          </button>
        )}
        <button
          type="button"
          data-testid={`${testId}-toggle`}
          onClick={() => setShow((s) => !s)}
          className="p-2 text-white/60 hover:text-[var(--gold)]"
          title={show ? 'Nascondi password' : 'Mostra password'}
          aria-label={show ? 'Nascondi password' : 'Mostra password'}
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
